import { z } from "zod";

// TES-886 — the subscription PLAN, which is not the clinical phase and not the
// entitlement status.
//
// Canonical source, Drive 1aBik1S3HxXG1cmSnbCnK4GYZ__XRSw2NmfMdHlE3t6E:
// "Abonnemang — Ella Uppstart och Ella Underhåll — webb- och produktkontrakt"
//
//   "Abonnemang och klinisk fas är oberoende."
//   "Klinisk fas får aldrig automatiskt ändra abonnemang."
//
// Three dimensions that keep being mistaken for each other:
//   clinicalPhase  — what the doctor decided (active_treatment | weight_stabilisation)
//   status         — whether Stripe is being paid (trialing | active | past_due | …)
//   plan           — WHICH product she is paying for. This file.
export const SUBSCRIPTION_PLANS = ["ella_uppstart", "ella_underhall"] as const;
export const subscriptionPlanSchema = z.enum(SUBSCRIPTION_PLANS);
export type SubscriptionPlan = z.infer<typeof subscriptionPlanSchema>;

// The plan is DERIVED FROM THE PRICE STRIPE ACTUALLY REPORTS, never from the
// clinical phase and never defaulted on a hunch.
//
// That direction matters. Stripe is where the money is, so the price on the
// subscription is the only fact about which product she is on that cannot
// drift from reality — a plan column written from anything else is a second
// source that will eventually disagree with the invoice she receives.
//
// `null` means the price is not one of ours: a legacy price, a campaign price,
// or a hand-made subscription at a price nobody registered. The caller decides
// what to do with that; this function refuses to guess, because guessing
// "uppstart" for an unknown price would silently claim coach access the
// patient may not be paying for.
export function planForPriceId(
  priceId: string | null | undefined,
  prices: { corePriceId?: string | null; maintenancePriceId?: string | null },
): SubscriptionPlan | null {
  if (!priceId) return null;
  // A placeholder is not a price. `price_TODO…` lives in tenant config before
  // a real Stripe product exists, and matching it would map every unknown
  // subscription onto whichever plan happened to hold the placeholder.
  const known = (candidate: string | null | undefined) =>
    typeof candidate === "string" &&
    candidate.length > 0 &&
    !candidate.startsWith("price_TODO") &&
    candidate === priceId;
  if (known(prices.corePriceId)) return "ella_uppstart";
  if (known(prices.maintenancePriceId)) return "ella_underhall";
  return null;
}

// The maintenance plan carries no ongoing personal coach; the starter one does.
//
//   "Ella Uppstart: coach_access=ACTIVE."
//   "Ella Underhåll: coach_access=INACTIVE; inga nya coachmeddelanden eller coachtriggers, tidigare coachchatt läsbar."
//
// Exported as the single reading of that rule so the surfaces cannot disagree.
// NOTE: nothing consumes this yet — the coach surfaces still show for every
// plan. Wiring it is its own piece of work, because "previous coach chat stays
// readable" is a different rule from "no new coach messages" and they have
// separate call sites.
export function coachAccessFor(plan: SubscriptionPlan): "active" | "inactive" {
  return plan === "ella_underhall" ? "inactive" : "active";
}

// TES-907 (Ö-221) — the doctor's explicit choice when he sends the link.
//
// Emelie 2026-09-23, on the label we asked about:
// "'Uppföljning' var en felaktig/oklar etikett och ska inte byggas som ett tredje val."
// So there are TWO options and no third, and the requirement from the same
// answer is "inget förvalt" — which is why `plan` has no default here and
// the server refuses a request without one rather than choosing.
//
// THE TWELVE-MONTH CONDITION. "Ella Underhåll" may be offered when the
// patient has
// "minst tolv månaders verifierad medicinsk viktbehandling totalt; tid hos tidigare vårdgivare får räknas",
// independently of clinical phase.
//
// We hold NO data about care given elsewhere — no column, no import, nothing.
// So the code cannot derive this and must not pretend to; deriving it from
// Ella's own activation date would quietly deny the plan to exactly the
// patients the rule was widened for. Simon 2026-09-24 decided the doctor
// confirms it, and states the basis. That is a human asserting a clinical
// fact, recorded — which is what "verifierad" asks for.
//
// TES-1073 (Ö-297) supersedes the "no data" half: the doctor now registers
// the earlier start (TreatmentStartRecord), and the server refuses an
// "Underhåll" link unless it shows twelve months (underhall-eligibility.ts).
// The stated basis is still required on top.
export const createSubscriptionLinkSchema = z
  .object({
    plan: subscriptionPlanSchema,
    // Required for `ella_underhall` and refused otherwise. Trimmed before the
    // length check so a field of spaces cannot pass for a basis.
    maintenanceBasis: z.string().trim().min(1).max(2000).optional(),
  })
  .superRefine((v, ctx) => {
    if (v.plan === "ella_underhall" && !v.maintenanceBasis) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["maintenanceBasis"],
        message: "maintenance_basis_required",
      });
    }
    if (v.plan !== "ella_underhall" && v.maintenanceBasis) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["maintenanceBasis"],
        message: "maintenance_basis_not_applicable",
      });
    }
  });
export type CreateSubscriptionLinkRequest = z.infer<
  typeof createSubscriptionLinkSchema
>;

// What the doctor sees BEFORE he sends it — Emelie asks for a preview.
//
// Derived from the plan rather than typed anywhere: the label, the price and
// the coach-access consequence are three facts about the product, and a
// preview that restated them by hand would be a fourth place for them to
// disagree with Stripe.
export const SUBSCRIPTION_PLAN_LABEL: Record<SubscriptionPlan, string> = {
  ella_uppstart: "Ella Uppstart",
  ella_underhall: "Ella Underhåll",
};

export const subscriptionLinkPreviewSchema = z.object({
  plan: subscriptionPlanSchema,
  label: z.string(),
  // Minor units, from the tenant's Stripe config — null when that plan has no
  // price configured, which the admin must surface rather than hide.
  amountMinor: z.number().int().nullable(),
  currency: z.string(),
  // `coachAccessFor`, so the doctor is told the consequence he is choosing.
  coachAccess: z.enum(["active", "inactive"]),
});
export type SubscriptionLinkPreview = z.infer<
  typeof subscriptionLinkPreviewSchema
>;
